import { Fragment, useState } from "react";
import { Typography, Input, Button, MenuItem, Divider } from "@mui/material";
import { tss } from "tss-react/mui";
import { alpha } from '@mui/material/styles';
import { CustomSelect, Section } from "components";
import { getCachedData } from "api/Cache";
import {
    filterInputTypes,
    getLayerInputValue,
    typeInFilter,
    getWorkflowsVersions,
    findSelectedObjects,
} from "actions/GlobalActions";
import { FoldersActions } from "actions";
import { getVersions } from "./AddObjectsList";
import ObjectInfo from "./ObjectInfo";
import ViewerEvents from "events/ViewerEvents";
import { handleLayerEvent } from "actions/ViewerTools";
import EventsEmitter from "events/EventsEmitter";
import commonStyles from "styles/common";

const useStyles = tss.create(({theme}) => ({
    sectionContent: {
        padding: 12,
        //background: alpha(theme.palette.background.paper, 0.75),
        boxShadow: "0px 10px 7px 0 #0000000d"
    },
    content: {
        padding: "2px 0px 8px 15px",
        boxShadow: "inset 0px 4px 3px 0px #0000000d",
        backgroundColor: "#f7f7f7",
        width: "100%",
    },
    label: {
        color: "#434343",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    row: {
        height: 25,
        display: "flex",
        alignItems: "center",
        width: "100%",
        position: "relative",
    },
    select: {
        width: "100%",
    },
    fullWidth: {
        width: "100%",
        fontSize: 14,
    },
    header: {
        textTransform: "capitalize",
        color: "#9c9c9c",
        fontSize: 11,
        lineHeight: 1.66666667,
        whiteSpace: "nowrap",
    },
    icon: {
        width: 24,
        height: 24,
    },
    layerIcon: {
        display: "inline-flex",
        alignItems: "center",
    },
    bottomContainer: {
        marginBottom: 0,
        paddingTop: 5,
        textAlign: "right",
    },
    list: {
        paddingLeft: 36,
    },
}));

function EntitiesSelect(props: { layer: any; input: any; entity: any; onChange: any }) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { layer, input, onChange } = props;
    var { entity } = props;
    if (!entity) {
        entity = { name: "", file_type: "UNKNOWN" };
    }
    const [value, setValue] = useState(entity);
    const [selectedEntities, setSelectedEntities] = useState({});

    const project: string = layer["project"];
    const entities = Object.values(getCachedData("entities")[project]);

    const voxels = entities.filter((entry: any) => entry["program_type"] === "VOXEL");
    var objects: any = [];

    if (entity && entity["file_type"]) {
        objects = entities.filter((entry: any) => entry["file_type"] === entity["file_type"]);
    }
    if (entity["file_type"] === "UNKNOWN") {
        const currentProject = ViewerEvents.currentProject;
        objects = Object.values(currentProject).filter((entry: any) => typeInFilter(entry, input));
    }
    const allObjects = getWorkflowsVersions(objects);
    const renderedObjects = findSelectedObjects(allObjects, selectedEntities);

    console.log("renderedObjects:", renderedObjects);
    const setEntity = (index: number, entry: any) => {
        setValue(entry);
        onChange({ target: { value: entry["ID"] } });
        const se: any = { ...selectedEntities };
        const workflow_id = FoldersActions.getEntityWorkflow(entry) || "unversioned";
        se[workflow_id] = entry;
        setSelectedEntities(se);
        EventsEmitter.emit("closeMenu", true);
    };
    return (
        <CustomSelect
            inputProps={{ style: { width: "100%", fontSize: 14 } }}
            menuListProps={{ style: { maxWidth: 280, marginLeft: -15 } }}
            value={value["name"]}
            className={common.customSelect}
            disableClose={true}
        >
            {voxels.length > 0 ? (
                <div>
                    <MenuItem className={classes.header}>Voxel Objects</MenuItem>
                    {voxels.map((object: any, index: number) => (
                        <ObjectInfo key={"voxel-" + index} index={index} object={object} setEntity={setEntity} />
                    ))}
                    <Divider />
                </div>
            ) : (
                ""
            )}
            {renderedObjects.map((object: any, index: number) => {
                const workflow_id = FoldersActions.getEntityWorkflow(object) || "unversioned";
                const versions = allObjects[workflow_id][object["name"]];
                return (
                    <ObjectInfo
                        key={"object-" + index}
                        index={index}
                        object={object}
                        setEntity={setEntity}
                        hideProducts={true}
                        hasChildren={true}
                    >
                        <div className={classes.list}>{getVersions(object, versions, setEntity)}</div>
                    </ObjectInfo>
                );
            })}
        </CustomSelect>
    );
}

function InputSection(props: { layer: any; input: any; setNeedsApply: any }) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { layer, input, setNeedsApply } = props;
    //console.log("input:", input);
    const inputType = parseInt(input["type"]);
    const label = input["label"];
    const id = input["id"];
    const inputValue = getLayerInputValue(layer, input);
    //console.log("inputValue:", inputValue);
    const [value, setValue] = useState(inputValue);

    const onChange = (evt: any) => {
        layer["input_value_" + id] = evt.target.value;
        setValue(evt.target.value);
        setNeedsApply(true);
    };

    return (
        <Fragment>
            <div className={common.row} style={{ marginTop: 10 }}>
                <Typography className={cx(common.label, common.subSectionLabel)}>{label}</Typography>
            </div>
            <div className={common.row} style={{ margin: "10px 0 5px 0" }}>
                {inputType === 0 ? (
                    <Input
                        type="number"
                        inputProps={{ min: 0, step: 0.1, max: 1 }}
                        value={value}
                        className={cx(common.numberInput, classes.fullWidth)}
                        onChange={onChange}
                    />
                ) : inputType === 2 ? (
                    <Input value={value} className={classes.fullWidth} onChange={onChange} />
                ) : (
                    <EntitiesSelect layer={layer} input={input} entity={inputValue} onChange={onChange} />
                )}
            </div>
        </Fragment>
    );
}

export default function InputsPanel(props: { layer: any }) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();

    const { layer } = props;
    const [needsApply, setNeeedsApply] = useState(false);
    const { inputs } = ViewerEvents;
    console.log("inputs:", inputs);
    const view_type = layer["view_type"];
    var curInputs: any = [];
    if (inputs && inputs !== null) {
        if (inputs[view_type]) {
            curInputs = inputs[view_type];
            curInputs = curInputs.filter(filterInputTypes);
        }
    }
    //console.log("curInputs:", curInputs);
    const applySettings = () => {
        handleLayerEvent("refreshView", null, layer);
        setNeeedsApply(false);
    };

    return (
        <Section name="inputsPanel" label="Inputs">
            <div className={classes.sectionContent} style={{ marginTop: -10 }}>
                {Object.values(
                    curInputs.map((input: any, index: number) => (
                        <InputSection
                            key={"input-" + index}
                            layer={layer}
                            input={input}
                            setNeedsApply={setNeeedsApply}
                        />
                    ))
                )}
            </div>
            {needsApply ? (
                <div className={cx(common.sectionContent, classes.bottomContainer)}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}
